import React, { useEffect, useRef, useState } from "react";
import { X, CloudSun, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import DateInput from "../../components/DateInput";
import { fmtDateGB } from "../../lib/format";

/**
 * Site-level weather (Open-Meteo GHI) backfill dialog.
 *
 * Two-step flow modelled on `SyncHistoryDialog`:
 *   1) Detect weather-data gaps (< 20 hourly GHI records / day) in the
 *      last N days so the operator can see the coverage picture.
 *   2) Pick a date range and pull hourly GHI from Open-Meteo (archive
 *      endpoint for anything > 5 days old, forecast endpoint otherwise).
 *      Existing `monitoring_15min` rows in the range are also updated
 *      with fresh ghi / estimated_kwh / variance_pct so historic variance
 *      overlays render correctly.
 */
export default function WeatherBackfillDialog({ projectId, onClose, onDone,
                                                  initialStartDate, initialEndDate }) {
  const [lookback, setLookback] = useState(90);
  const [gaps, setGaps] = useState(null);
  const [loadingGaps, setLoadingGaps] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(initialStartDate || ninetyDaysAgo);
  const [endDate, setEndDate] = useState(initialEndDate || today);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const scanIdRef = useRef(0);

  const detectGaps = async () => {
    const myId = ++scanIdRef.current;
    setLoadingGaps(true);
    try {
      const { data } = await api.get(
        `/monitoring/${projectId}/weather-gaps?lookback_days=${lookback}`,
      );
      if (myId !== scanIdRef.current) return;
      setGaps(data);
    } catch (e) {
      if (myId !== scanIdRef.current) return;
      toast.error(formatApiError(e.response?.data?.detail) || "Gap scan failed");
    } finally {
      if (myId === scanIdRef.current) setLoadingGaps(false);
    }
  };

  useEffect(() => { detectGaps(); /* eslint-disable-next-line */ }, [projectId, lookback]);

  const runBackfill = async () => {
    if (!startDate || !endDate) return toast.error("Pick a start and end date");
    if (endDate < startDate) return toast.error("End date must be on or after start date");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/monitoring/${projectId}/backfill-weather`,
        { start_date: startDate, end_date: endDate },
      );
      setResult(data);
      if (data.errors && data.errors.length > 0) {
        toast.warning(`Weather backfill finished with ${data.errors.length} error(s) — see details below`);
      } else {
        toast.success(`Wrote ${data.hours_written.toLocaleString()} hourly GHI record(s)`);
      }
      detectGaps();
      if (onDone) onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Weather backfill failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="weather-backfill-dialog">
      <div className="bg-white w-full max-w-3xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label flex items-center gap-2">
              <CloudSun size={11} /> Weather backfill · Open-Meteo GHI
            </div>
            <div className="font-display text-xl font-semibold mt-1">
              Reconstruct historic weather &amp; variance
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"
                  data-testid="weather-backfill-close-btn"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm muted leading-relaxed">
            Fetches hourly Global Horizontal Irradiance from Open-Meteo (archive endpoint for anything &gt; 5 days old — up to 5 years back) and stores it against this site. Historic monitoring rows in the same range get re-scored with fresh estimated production &amp; variance.
          </p>

          {/* Gap detection */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="label">1. Weather gap scan</div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] muted">Look back</label>
                <select className="input-swiss text-xs py-1"
                        value={lookback}
                        onChange={(e) => setLookback(Number(e.target.value))}
                        data-testid="weather-gap-lookback-select">
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                  <option value={730}>2 years</option>
                  <option value={1826}>5 years</option>
                </select>
                <button className="btn-ghost text-[10px]"
                        onClick={detectGaps} disabled={loadingGaps}
                        data-testid="weather-gap-rescan-btn">
                  <RefreshCw size={11} /> Re-scan
                </button>
              </div>
            </div>

            {loadingGaps ? (
              <div className="text-xs muted">Scanning…</div>
            ) : gaps ? (
              gaps.days_with_gaps === 0 ? (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2"
                     data-testid="weather-gap-none">
                  <CheckCircle2 size={14} />
                  No weather gaps in the last {lookback} days — every day has ≥ 20 hourly records.
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200"
                     data-testid="weather-gap-list">
                  <div className="text-xs text-amber-900 flex items-center gap-2 mb-2 font-mono">
                    <AlertTriangle size={13} />
                    {gaps.days_with_gaps} day(s) missing weather data
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {gaps.gaps.map((g) => (
                      <span key={g.date}
                            className="text-[10px] font-mono px-1.5 py-0.5 bg-white border border-amber-300 text-amber-900"
                            title={`${g.hours} of 24 hourly records`}>
                        {fmtDateGB(g.date)} <span className="muted">({g.hours})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )
            ) : null}
          </section>

          {/* Backfill controls */}
          <section>
            <div className="label mb-3">2. Backfill range</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label mb-1 block">Start date</span>
                <DateInput className="input-swiss w-full font-mono text-xs"
                       value={startDate}
                       onChange={(e) => setStartDate(e.target.value)}
                       max={today}
                       data-testid="weather-backfill-start-date" />
              </label>
              <label className="block">
                <span className="label mb-1 block">End date</span>
                <DateInput className="input-swiss w-full font-mono text-xs"
                       value={endDate}
                       onChange={(e) => setEndDate(e.target.value)}
                       max={today}
                       data-testid="weather-backfill-end-date" />
              </label>
            </div>
            <div className="p-3 bg-[var(--surface-2)] border border-[var(--line)] text-[11px] muted mt-3">
              Open-Meteo is free — no API key required. Upserts are idempotent, safe to re-run.
            </div>
          </section>

          {/* Result */}
          {result && (
            <section className="p-4 border border-[var(--line)] bg-[var(--surface-2)]"
                     data-testid="weather-backfill-result">
              <div className="label mb-2">Last run</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="muted">Hours written</div>
                  <div className="font-mono text-lg">{result.hours_written?.toLocaleString?.() || 0}</div>
                </div>
                <div>
                  <div className="muted">Monitoring rows re-scored</div>
                  <div className="font-mono text-lg">{result.monitoring_rows_updated?.toLocaleString?.() || 0}</div>
                </div>
                <div>
                  <div className="muted">Errors</div>
                  <div className={`font-mono text-lg ${result.errors?.length ? "text-red-600" : ""}`}>
                    {result.errors?.length || 0}
                  </div>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-3 text-[11px] font-mono text-red-700 list-disc pl-4 max-h-24 overflow-y-auto">
                  {result.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </section>
          )}
        </div>

        <div className="flex justify-end items-center gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost"
                  data-testid="weather-backfill-cancel-btn">Close</button>
          <button onClick={runBackfill} disabled={busy}
                  className="btn-primary bg-[var(--accent)] border-[var(--accent)]"
                  data-testid="run-weather-backfill-btn">
            <CloudSun size={13} /> {busy ? "Backfilling…" : "Run weather backfill"}
          </button>
        </div>
      </div>
    </div>
  );
}
